import { useState } from 'react';
import { useDepartmentOverdue, useTaskDepartments, type Department } from '../../api/taskQueries.ts';
import { TaskRow } from './TaskRow.tsx';
import { TaskCard } from './TaskCard.tsx';
import { agencyToday } from './FollowUp.tsx';
import { overdueTabId } from '../../tasks/overdueTab.ts';

/**
 * Overdue work by department (Arthur, A-#83 A5; A-#145): every open overdue task in a department, whoever holds it,
 * one TAB per department, never a stack of expanding lists. Which departments is the server's list, and each tab's
 * read asks may_open_task_queue() again (GET /tasks/department-overdue), so the queue and this view never disagree:
 *  - "own" (My tasks and the directors' landings): own_overdue, the reader's own department only. A director has one,
 *    drawn with no tab bar; tiers 1-2 with no department, the Executive Case Reviewer and a case manager have none,
 *    and nothing is drawn;
 *  - "all" (Overdue by department, tiers 1-2): department_overdue, every department the reader may open.
 * Read-only: a row opens the task card, which offers Reassign only where the task's may allows. Nothing here is
 * reported seen: these are other people's tasks.
 */
export function DepartmentOverduePanel({ which = 'own', className = 'mb-4' }: { which?: 'own' | 'all'; className?: string }) {
  const departments = useTaskDepartments();
  const list = (which === 'all' ? departments.data?.department_overdue : departments.data?.own_overdue) ?? [];
  const [chosen, setChosen] = useState<Department | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  if (list.length === 0) return null;
  const tab = chosen !== null && list.includes(chosen) ? chosen : list[0]!;
  return (
    <section aria-labelledby="dept-overdue" className={`panel ${className}`}>
      <header className="panel-bar">
        <h2 id="dept-overdue">{list.length === 1 ? `Overdue in ${list[0]}` : 'Overdue by department'}</h2>
      </header>
      {list.length > 1 && (
        <div role="tablist" aria-label="Departments" className="no-scrollbar flex gap-1 overflow-x-auto border-b border-neutral-200 px-2 lg:px-3">
          {list.map((d) => (
            <button key={d} type="button" role="tab" id={overdueTabId(d)} aria-selected={d === tab} aria-controls="overdue-tabpanel"
                    onClick={() => setChosen(d)}
                    className={`flex min-h-[44px] shrink-0 items-center px-3 text-sm font-semibold ${
                      d === tab ? 'text-neutral-900 underline decoration-accent decoration-[3px] underline-offset-[8px]' : 'text-neutral-700 hover:text-neutral-900'}`}>
              {d}
            </button>
          ))}
        </div>
      )}
      <div id="overdue-tabpanel" role={list.length > 1 ? 'tabpanel' : undefined}
           aria-labelledby={list.length > 1 ? overdueTabId(tab) : undefined}>
        <DepartmentTasks key={tab} department={tab} onOpen={setOpen} />
      </div>
      {open && <TaskCard taskId={open} onClose={() => setOpen(null)} />}
    </section>
  );
}

function DepartmentTasks({ department, onOpen }: { department: Department; onOpen: (id: string) => void }) {
  const q = useDepartmentOverdue(department);
  const tasks = q.data?.tasks ?? [];
  const today = q.data?.today ?? agencyToday();
  return q.error && !q.data ? (
    <p role="alert" className="px-4 py-3 font-semibold text-critical-edge lg:px-5">The overdue work could not be loaded.</p>
  ) : !q.data ? (
    <p className="px-4 py-3 text-neutral-700 lg:px-5">Loading…</p>
  ) : tasks.length === 0 ? (
    <p className="px-4 py-3 text-neutral-700 lg:px-5">Nothing in {department} is overdue.</p>
  ) : (
    <>
      <p className="px-4 pt-2 text-sm text-neutral-700 tabular-nums lg:px-5">{tasks.length} overdue</p>
      <ol className="divide-y divide-neutral-200">
        {tasks.map((t) => <TaskRow key={t.id} task={t} today={today} onOpen={onOpen} showHolder />)}
      </ol>
    </>
  );
}
